import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import type { ReactNode } from 'react';
import {
  maskCapturedNetworkRequest,
  maskPostHogCaptureUrls,
} from '@/integrations/posthog/maskCapturedNetworkRequest';
import { registerBasePostHogSuperProperties } from '@/integrations/posthog/utils';
import { environment } from '@/utils/env.utils';

posthog.init(environment.posthogKey, {
  api_host: environment.posthogApiHost,
  ui_host: environment.posthogUiHost,
  defaults: '2026-01-30',
  // Never create person profiles for anonymous (not logged-in) users.
  person_profiles: 'identified_only',
  session_recording: {
    maskTextSelector: '[data-private]',
    maskAllInputs: true,
    maskInputFn: (text, element) => {
      const isPassword = element instanceof HTMLInputElement && element.type === 'password';

      if (isPassword || element?.dataset.private === 'true') {
        return '*'.repeat(text.length);
      }

      return text;
    },
    // Scrub PII / auth secrets in captured network URLs and bodies, and drop Loqate requests.
    // Bodies and headers remain enabled via project settings — do not disable them here.
    // A custom mask fn replaces PostHog automatic body redaction (headers are still
    // hard-denied by the SDK).
    maskCapturedNetworkRequestFn: maskCapturedNetworkRequest,
  },
  // Replay masking does not cover analytics `$current_url`. Reuse the same URL
  // scrubber here instead of `mask_personal_data_properties`, which would also
  // mask advertising click IDs (gclid/fbclid) used for attribution.
  before_send: maskPostHogCaptureUrls,
  // Improves marketing-site -> client session handoff (details: https://fairloo.atlassian.net/browse/EC-1018)
  __preview_cookie_wins_on_conflict: true,
});

const isSupportSession = new URLSearchParams(window.location.search).get('support_session') === 'true';

if (isSupportSession) {
  posthog.reset();
}

registerBasePostHogSuperProperties();

if (isSupportSession) {
  posthog.register_for_session({ support_session: true });
}

export { posthog };

export const AppPostHogProvider = ({ children }: { children: ReactNode }) => (
  <PostHogProvider client={posthog}>{children}</PostHogProvider>
);
